import React, { useState, useEffect, useCallback } from 'react';
import { apiGet, apiDelete } from '../api';

const LiveStreamsManager = () => {
    const [liveStreams, setLiveStreams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [streamToDelete, setStreamToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchStreams = useCallback(async () => {
        try {
            setLiveStreams(await apiGet('/api/admin/live-rooms'));
        } catch {
            setError("Failed to load live streams.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStreams();
        const t = setInterval(fetchStreams, 15000); // light polling (was a live snapshot)
        return () => clearInterval(t);
    }, [fetchStreams]);

    const handleOpenDeleteModal = (stream) => { setStreamToDelete(stream); setIsModalOpen(true); };
    const handleCloseModal = () => { setIsModalOpen(false); setStreamToDelete(null); };

    const handleConfirmDelete = async () => {
        if (!streamToDelete) return;
        setIsDeleting(true);
        try {
            const result = await apiDelete(`/api/admin/rooms/${streamToDelete.id}`);
            if (result.success === false) throw new Error(result.message);
            alert(result.message);
            fetchStreams();
        } catch (err) {
            alert(`Failed to delete stream: ${err.message}`);
        } finally {
            setIsDeleting(false);
            handleCloseModal();
        }
    };

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading live streams...</p></div>;
    if (error) return <div className="dark-error">{error}</div>;

    return (
        <div className="dark-page">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <h2 style={{ margin: 0 }}>Manage Live Streams</h2>
                <span className="badge badge-red" style={{ fontSize: 13 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ff4d6d', display: 'inline-block', marginRight: 6, animation: 'pulse 1.5s infinite' }} />
                    {liveStreams.length} Live
                </span>
            </div>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th>Host Name</th>
                            <th>User ID</th>
                            <th>Room Type</th>
                            <th className="text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {liveStreams.length > 0 ? liveStreams.map(stream => (
                            <tr key={stream.id}>
                                <td className="font-medium">{stream.hostName || 'N/A'}</td>
                                <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>{stream.IDN}</td>
                                <td>
                                    <span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{stream.roomType || 'N/A'}</span>
                                </td>
                                <td className="text-center">
                                    <button onClick={() => handleOpenDeleteModal(stream)} className="btn-action delete">
                                        <i className="fas fa-trash-alt" style={{ marginRight: 6 }} />Force Delete
                                    </button>
                                </td>
                            </tr>
                        )) : (
                            <tr className="empty-row">
                                <td colSpan="4">No active live streams.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirm Modal */}
            {isModalOpen && (
                <div className="modal">
                    <div className="modal-content" style={{ maxWidth: 400 }}>
                        <div style={{ textAlign: 'right' }}>
                            <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                        <div style={{ textAlign: 'center', padding: '0 16px 16px' }}>
                            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <i className="fas fa-exclamation-triangle" style={{ color: '#f87171', fontSize: 24 }} />
                            </div>
                            <h3 style={{ fontWeight: 700, marginBottom: 10 }}>Confirm Force Deletion</h3>
                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                                Are you sure you want to delete the live stream by <strong style={{ color: '#f87171' }}>{streamToDelete?.hostName}</strong>?
                                This will remove the room and all its data permanently. <strong>This action cannot be undone.</strong>
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
                                <button onClick={handleCloseModal} disabled={isDeleting} className="cancelbtn">
                                    Cancel
                                </button>
                                <button onClick={handleConfirmDelete} disabled={isDeleting} className="signupbtn" style={{ background: 'linear-gradient(135deg,#ef4444,#dc2626)' }}>
                                    {isDeleting ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Deleting...</> : 'Confirm Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveStreamsManager;
